import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBarsFilter, faArrowUpRight } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck, faArrowDown } from '@fortawesome/pro-solid-svg-icons';
import { usePartnerCompaniesWithStatsQuery, type CompanyListPage } from '../../hooks/requests/queries/use_partner_companies_with_stats_query';
import type { PartnerOrganization } from '../../hooks/requests/queries/use_partner_organizations_query';
import type { DateRangeValue } from '@ui';
import { OngoingCloseBadge, FilterLabelRow } from '@ui';
import { getDateLabel, isOngoingPeriod } from './date_range_label';

// ── Types ──────────────────────────────────────────────────────────────────────

export type CompanyOverviewTableProps = {
  isAceUpStaff: boolean;
  /** Omit for Partner roles — a single-org caller has no "which partner" to show. */
  orgLabel?: string;
  companyLabel?: string;
  organizations?: PartnerOrganization[];
  /**
   * AceUp staff only — the org currently selected in the dropdown. Scopes the
   * request server-side and re-fetches whenever the selection changes. Not
   * needed for Partner roles, whose org_id always comes from the JWT.
   */
  selectedOrganizationId?: string | null;
  /**
   * Company filter dropdown selection. When non-empty, the table only shows
   * rows for these companies (client-side — the stats endpoint is fetched
   * unscoped for the org, same as before).
   */
  selectedCompanyIds?: string[];
  /** Date range filter selection — drives the date label and Ongoing/Closed badge. */
  dateRange?: DateRangeValue | null;
};

type SortField =
  | 'company'
  | 'partner'
  | 'total_users'
  | 'total_messages'
  | 'msg_per_user'
  | 'last_active'
  | 'created_on';
type SortDir = 'asc' | 'desc';

type CompanyRow = {
  companyId: string;
  companyName: string;
  organizationId: string;
  totalUsers: number;
  totalMessages: number;
  msgPerUser: number | null;
  lastActive: string | null;
  createdOn: string | null;
};

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

// ── Row helpers ────────────────────────────────────────────────────────────────

const formatNumber = (n: number): string => n.toLocaleString();

const formatAvg = (n: number | null): string => {
  if (n === null) return '—';
  return n % 1 === 0 ? String(n) : n.toFixed(1);
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateShort = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

function pageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [];
  if (current <= 3) {
    pages.push(1, 2, 3, 4, '...', total);
  } else if (current >= total - 2) {
    pages.push(1, '...', total - 3, total - 2, total - 1, total);
  } else {
    pages.push(1, '...', current - 1, current, current + 1, '...', total);
  }
  return pages;
}

// ── Component ──────────────────────────────────────────────────────────────────

const CompanyOverviewTable: React.FC<CompanyOverviewTableProps> = ({
  isAceUpStaff,
  orgLabel,
  companyLabel,
  organizations,
  selectedOrganizationId,
  selectedCompanyIds,
  dateRange,
}) => {
  const navigate = useNavigate();

  const defaultSort: SortField = isAceUpStaff ? 'company' : 'total_users';
  const defaultDir: SortDir = isAceUpStaff ? 'asc' : 'desc';

  const [sortField, setSortField] = useState<SortField>(defaultSort);
  const [sortDir, setSortDir] = useState<SortDir>(defaultDir);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Fetch all companies once; paginate client-side to avoid per-page API calls.
  // AceUp staff: scope to the selected org and hold off until one is picked
  // (there's no "All orgs" view). Partner roles: org comes from the JWT.
  //
  // `from`/`to` must be forwarded here — the backend's totalUsers/totalMessages
  // per company are scoped to whatever date range it's given (defaulting to a
  // trailing 30 days when omitted, per usePartnerCompaniesWithStatsQuery's own
  // doc comment). Without this, the table silently ignored the page's Date
  // Range filter and showed that trailing-30-day window regardless of what
  // was selected — which is why its numbers didn't match the (correctly
  // scoped) summary widgets above it.
  const { data: companiesData, isLoading: companiesIsLoading, isError: companiesIsError } =
    usePartnerCompaniesWithStatsQuery({
      limit: 100,
      organizationId: isAceUpStaff ? selectedOrganizationId : undefined,
      from: dateRange ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
      to: dateRange ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
      enabled: !isAceUpStaff || Boolean(selectedOrganizationId),
    });

  // Date label — same logic as the summary widgets (e.g. "July" for the
  // current month, "June" for last month), driven by the actual date
  // filter selection rather than whatever range the stats endpoint resolved.
  const dateLabel = getDateLabel(dateRange);
  const ongoing = isOngoingPeriod(dateRange);

  // organizationId → display name lookup
  const orgNameById = useMemo(() => {
    const map = new Map<string, string>();
    organizations?.forEach((o) => {
      map.set(o.organizationId, o.organizationName?.trim() || o.organizationId);
    });
    return map;
  }, [organizations]);

  const allRows: CompanyRow[] = useMemo(() => {
    if (!companiesData) return [];
    const rows = companiesData.companies.map((c) => ({
      companyId: c.companyId,
      companyName: c.companyName,
      organizationId: c.organizationId,
      totalUsers: c.totalUsers,
      totalMessages: c.totalMessages,
      msgPerUser: c.totalUsers > 0 ? c.totalMessages / c.totalUsers : null,
      lastActive: c.lastActive,
      createdOn: c.createdOn,
    }));

    // Company filter dropdown selection — narrow the table to just those
    // companies when any are selected.
    if (!selectedCompanyIds?.length) return rows;
    const selected = new Set(selectedCompanyIds);
    return rows.filter((r) => selected.has(r.companyId));
  }, [companiesData, selectedCompanyIds]);

  // Sort all rows, then slice for the current page
  const sortedRows = useMemo(() => {
    const sorted = [...allRows].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'company':
          cmp = a.companyName.localeCompare(b.companyName);
          break;
        case 'partner':
          cmp = (orgNameById.get(a.organizationId) ?? a.organizationId)
            .localeCompare(orgNameById.get(b.organizationId) ?? b.organizationId);
          break;
        case 'total_users':
          cmp = a.totalUsers - b.totalUsers;
          break;
        case 'total_messages':
          cmp = a.totalMessages - b.totalMessages;
          break;
        case 'msg_per_user':
          cmp = (a.msgPerUser ?? 0) - (b.msgPerUser ?? 0);
          break;
        case 'last_active':
          cmp = (a.lastActive ?? '').localeCompare(b.lastActive ?? '');
          break;
        case 'created_on':
          cmp = (a.createdOn ?? '').localeCompare(b.createdOn ?? '');
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    const start = (currentPage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [allRows, sortField, sortDir, currentPage, pageSize, orgNameById]);

  // Client-side pagination counts
  const totalItems = allRows.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Drill-down navigation (sc-43503) — AceUp staff must carry the selected
  // org through the URL so the drill-down page doesn't have to guess which
  // organization the clicked company belongs to.
  const goToCompany = (row: CompanyRow) => {
    const query = isAceUpStaff ? `?organizationId=${encodeURIComponent(row.organizationId)}` : '';
    navigate(`/companies/${encodeURIComponent(row.companyId)}${query}`);
  };

  const handlePageSize = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Reset back to page 1 whenever the selected org or company filter changes
  // — the previous page number may no longer exist for a smaller list.
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedOrganizationId, selectedCompanyIds]);

  // ── Sub-components ───────────────────────────────────────────────────────────

  const Th = ({
    field,
    label,
    first = false,
  }: {
    field: SortField;
    label: string;
    first?: boolean;
  }) => {
    const active = sortField === field;
    return (
      <th className={`cot-th${first ? ' cot-th--first' : ''}`}>
        <button
          type="button"
          className={`cot-th-btn${active ? ' cot-th-btn--active' : ''}`}
          onClick={() => handleSort(field)}
        >
          <span className="cot-th-text">{label}</span>
          {active ? (
            <FontAwesomeIcon
              icon={faArrowDown}
              className={`cot-sort-arrow${sortDir === 'asc' ? ' cot-sort-arrow--asc' : ''}`}
            />
          ) : (
            <span className="cot-sort-inactive">↕</span>
          )}
        </button>
      </th>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="cot-card">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="cot-header">
        <p className="cot-eyebrow">Company overview</p>

        <div className="suc-filter-row">
          <FilterLabelRow orgLabel={orgLabel} companyLabel={companyLabel} dateLabel={dateLabel} />
          <OngoingCloseBadge isOngoing={ongoing} />
        </div>
      </div>

      {/* ── Table settings bar ─────────────────────────────────── */}
      <div className="cot-settings-bar">
        <p className="cot-count">
          <strong>{totalItems}</strong>
          {totalItems === 1 ? ' company' : ' companies'}
        </p>

        <div className="cot-settings-right">
          <span className="cot-show-label">Show:</span>

          <div className="cot-page-size-btn">
            <div className="cot-page-size-left">
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                className="cot-tablerows-icon"
              >
                <rect x="1" y="2" width="10" height="2" rx="0.5" fill="currentColor" />
                <rect x="1" y="5" width="10" height="2" rx="0.5" fill="currentColor" />
                <rect x="1" y="8" width="10" height="2" rx="0.5" fill="currentColor" />
              </svg>
              <select
                value={pageSize}
                onChange={(e) => handlePageSize(Number(e.target.value))}
                className="cot-page-size-select"
                aria-label="Rows per page"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={`page-size-${n}`} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              className="cot-page-size-chevron"
              aria-hidden
            >
              <path
                d="M2 3.5l3 3 3-3"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div className="cot-pagination">
            <button
              type="button"
              className="cot-pg-arrow"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              aria-label="Previous page"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path
                  d="M6 2L3.5 5 6 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {pageNumbers(currentPage, totalPages).map((p, i) =>
              p === '...' ? (
                <span key={`ellipsis-${i}`} className="cot-pg-ellipsis">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  className={`cot-pg-num${currentPage === p ? ' cot-pg-num--active' : ''}`}
                  onClick={() => setCurrentPage(p as number)}
                  aria-current={currentPage === p ? 'page' : undefined}
                >
                  {p}
                </button>
              )
            )}

            <button
              type="button"
              className="cot-pg-arrow"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              aria-label="Next page"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path
                  d="M4 2L6.5 5 4 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ── Table ──────────────────────────────────────────────── */}
      <div className="cot-table-wrap">
        <table className="cot-table">
          <colgroup>
            <col className="cot-col-company" />
            {isAceUpStaff && <col className="cot-col-partner" />}
            <col className="cot-col-users" />
            <col className="cot-col-messages" />
            <col className="cot-col-avg" />
            <col className="cot-col-lastactive" />
            <col className="cot-col-createdon" />
          </colgroup>
          <thead>
            <tr>
              <Th field="company" label="Company" first />
              {isAceUpStaff && <Th field="partner" label="Partner" />}
              <Th field="total_users" label="Total users" />
              <Th field="total_messages" label="Total messages" />
              <Th field="msg_per_user" label="Msg / user (avg)" />
              <Th field="last_active" label="Last active" />
              <Th field="created_on" label="Created on" />
            </tr>
          </thead>
          <tbody>
            {companiesIsLoading ? (
              <tr>
                <td colSpan={isAceUpStaff ? 7 : 6} className="cot-empty-cell">
                  Loading…
                </td>
              </tr>
            ) : companiesIsError ? (
              <tr>
                <td colSpan={isAceUpStaff ? 7 : 6} className="cot-empty-cell">
                  Failed to load companies
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={isAceUpStaff ? 7 : 6} className="cot-empty-cell">
                  No companies to display
                </td>
              </tr>
            ) : (
              sortedRows.map((row, index) => (
                <tr
                  key={`${row.organizationId}:${index}`}
                  className="cot-row"
                  onClick={() => goToCompany(row)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      goToCompany(row);
                    }
                  }}
                  role="link"
                  tabIndex={0}
                >
                  <td className="cot-td cot-td--first">
                    <div className="cot-company-inner">
                      <span className="cot-company-name">{row.companyName}</span>
                      <FontAwesomeIcon icon={faArrowUpRight} className="cot-company-arrow" />
                    </div>
                  </td>
                  {isAceUpStaff && (
                    <td className="cot-td cot-td--muted">
                      {orgNameById.get(row.organizationId) ?? row.organizationId}
                    </td>
                  )}
                  <td className="cot-td">{formatNumber(row.totalUsers)}</td>
                  <td className="cot-td">{formatNumber(row.totalMessages)}</td>
                  <td className="cot-td">{formatAvg(row.msgPerUser)}</td>
                  <td className="cot-td cot-td--muted">{formatDateShort(row.lastActive)}</td>
                  <td className="cot-td cot-td--muted">{formatDate(row.createdOn)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompanyOverviewTable;
